<template>
  <div class="relative flex flex-col">
    <div class="flex h-[54px] items-center justify-between bg-[--v3-input-lists-inputlistheaderbg] pl-[14px] pr-[8px] pt-[5px]">
      <div class="flex items-center gap-[8px]">
        <div
          v-if="canReturn"
          class="flex size-[14px] cursor-pointer items-center justify-center"
          @click="$emit('return')"
        >
          <Icon name="angle-left" class="h-[12px] w-[7px] text-[--v3-surface-surface-900]" />
        </div>
        <div
          class="block size-[12px] shrink-0 rounded-full text-inherit"
          :style="{
            'background-color': color,
          }"
        />
        <button
          type="button"
          class="v3-body-semibold-600-line-height-1.5 cursor-pointer text-[--v3-global-textcolor] transition-colors duration-150 hover:text-[--v3-global-textsecondarycolor]"
          @click="handleOpenListPage"
        >
          {{ listName }}
        </button>
      </div>
      <Button
        outlined
        label="Copy"
        icon="copy"
        icon-class="size-[14px]"
        @click="handleCopy"
      />
    </div>
    <div ref="scrollContainer" class="flex-1 bg-[--v3-input-lists-inputlistheaderbg]">
      <PerfectScrollbar ref="scroll">
        <KeywordSliderItem
          v-for="{ id, keyword } in keywords"
          :key="id"
          :name="keyword"
          @delete="handleDeleteKeyword(id)"
        />
      </PerfectScrollbar>
    </div>
    <div class="flex flex-col items-end gap-[12px] bg-[--v3-input-lists-inputlistheaderbg] py-[12px] pl-[30px] pr-[16px]">
      <Divider class="m-0" />
      <Button
        severity="warning"
        class="w-fit"
        label="Delete List"
        icon="trash"
        icon-class="size-[14px]"
        outlined
        @click="$emit('attemptDeleteList')"
      />
    </div>
    <Transition name="fade">
      <KeywordListConfirmDeletePanel
        v-if="isAttemptingDelete"
        class="absolute left-0 top-0 size-full justify-center"
        :list="listName"
        :loading="isDeleting"
        :keyword="deletingKeyword"
        @cancel="$emit('cancel')"
        @confirm="$emit('confirm')"
      />
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { useElementSize } from '@vueuse/core';
import { useClipboard } from '@vueuse/core';
import Divider from 'primevue/divider';
import { computed,useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

import Button from '@/common/components/atoms/Button';
import Icon from '@/common/components/atoms/Icon';
import KeywordListConfirmDeletePanel from '@/common/components/molecules/KeywordListConfirmDeletePanel';
import KeywordSliderItem from '@/common/components/molecules/KeywordSliderItem';
import { useTrackedToast } from '@/composables/useTrackedToast';

const props = withDefaults(
  defineProps<{
    listId: number,
    listName: string,
    keywords: { id: number, keyword: string }[],
    color: string,
    isAttemptingDelete?: boolean,
    isDeleting?: boolean,
    canReturn?: boolean,
    deletingKeyword?: string | null | undefined,
  }>(),
  {
    isDeleting: false,
    canReturn: false,
    deletingKeyword: undefined,
  },
);

const emit = defineEmits<{
  attemptDeleteList: [];
  attemptDeleteKeyword: [keywordId: number];
  cancel: [];
  confirm: [];
  openListPage: [];
  return: [];
}>();

const scroll = useTemplateRef('scroll');
const container = useTemplateRef('scrollContainer');
const { height } = useElementSize(container);
const scrollHeight = computed(() => `${height.value}px`);

const toast = useTrackedToast();
const router = useRouter();
const { copy } = useClipboard({
  legacy: true,
});

const keywordsCSV = computed(() => props.keywords.map(({ keyword }) => keyword).join(','));

watch(() => props.listName, () => {
  scroll.value?.ps?.element && (scroll.value.ps.element.scrollTop = 0);
  scroll.value?.ps?.update();
});

const handleDeleteKeyword = (keywordId: number) => {
  emit('attemptDeleteKeyword', keywordId);
};

const handleCopy = () => {
  copy(keywordsCSV.value);
  toast.add({ severity: 'success', summary: 'Copied!', life: 3000 });
};

const handleOpenListPage = () => {
  emit('openListPage');
  void router.push({
    name: 'keyword-lists',
    query: {
      id: String(props.listId),
    },
  });
};
</script>

<style scoped>
.ps {
  max-height: v-bind(scrollHeight);
}

.fade-enter-active,
.fade-leave-active {
  @apply transition-opacity duration-150;
}

.fade-enter-from,
.fade-leave-to {
  @apply opacity-0;
}

.fade-enter-to,
.fade-leave-from {
  @apply opacity-100;
}
</style>
